import i18n from 'i18next'
import React from 'react'
import { useTranslation } from 'react-i18next'
import { useMountedState } from 'react-use'

import { graphql } from '~/gql'
import { type OutputType, type SegmentMode } from '~/gql/graphql'
import { useGraphQLRequest } from '~/hooks/useGraphQLRequest'
import { useSurveyQueryData } from '~/hooks/useSurveyQuery/useSurveyQueryData'
import { toOutputLanguage } from '~/locales'
import {
  assertExists,
  buildSurveyFilterTextGroups,
  exists,
  formatFilterTextGroups,
  type SurveyFilterGroups,
} from '~/utils'

import { isCustomQuestionRole } from '../atoms/stateAtoms'

import { createTaskElementsBuilder } from './buildTaskElements'
import { planRenderTasks, type RenderTask } from './planRenderTasks'
import { type BasicOutputsResult } from './useGenerateBasicOutputs'

// The wizard/image segmentMode vocabulary ('5segs' / '9segs') differs from the
// comment subsystem's GraphQL `SegmentMode` enum ('fiveSegs' / 'nineSegs'). The
// analyzer types AnalysisComment(Input).segmentMode as that enum, so the comment
// INPUT, the returned segmentMode, and the lookup key must all use the enum
// vocabulary. Map at the boundary (same table as useRenderAndSaveDocumentImages'
// segmentModeToDisplayType).
const wizardSegmentModeToEnum: Record<string, SegmentMode> = {
  '5segs': 'fiveSegs',
  '9segs': 'nineSegs',
}

// Callers only ever pass the wizard tags '5segs' / '9segs' (segmentMode always
// round-trips), so the `?? 'none'` fallback is unreachable; it exists only to
// keep the return type a valid `SegmentMode` member for the tightened enum.
export const toCommentSegmentMode = (wizardSegmentMode: string): SegmentMode =>
  wizardSegmentModeToEnum[wizardSegmentMode] ?? 'none'

// Map a planned RenderTask to the OutputType enum member carried on its
// AnalysisCommentInput. A CUSTOM standard role has no dedicated enum member or
// prompt, so it maps to the bare `standardAnalysis` (→ dispatch.ts generic);
// builtin standard + customized roles map to their `<analysis>_<role>` member.
// Identity is carried by outputKey, NOT this value — several custom roles share
// the bare member. Every arm returns an OutputType string literal, so the
// compiler guarantees the result is a real enum member (no cast).
export const toCommentOutputType = (task: RenderTask): OutputType => {
  if (task.kind === 'customer') {
    return 'customerAnalysis'
  }

  // standard/customized always carry a role (planRenderTasks invariant); a
  // missing role is a real planning bug → fail fast.
  if (task.role == null) {
    throw new Error(
      `basic-output: ${task.kind} task missing role (outputKey=${task.outputKey})`,
    )
  }

  if (task.kind === 'standard') {
    // Must return BEFORE the role switch below: a custom role name matches nothing there, so
    // control would drop into the `customized` switch and throw.
    if (task.axis === 'customerAttribute') {
      return 'standardAnalysisCustomerAttribute'
    }

    switch (task.role) {
      case 'npiDriver':
        return 'standardAnalysis_npiDriver'
      case 'importance':
        return 'standardAnalysis_importance'
      case 'trigger':
        return 'standardAnalysis_trigger'
      case 'resistance':
        return 'standardAnalysis_resistance'
      default:
        // Any active non-builtin standard role is a custom question role; it has
        // no dedicated prompt → bare standardAnalysis (generic). Guard the intent
        // so a typo'd/unexpected role fails fast instead of silently routing to
        // generic.
        if (!isCustomQuestionRole(task.role)) {
          throw new Error(`basic-output: unexpected standard role ${task.role}`)
        }

        return 'standardAnalysis'
    }
  }

  // kind === 'customized': the wizard only ever emits npiDriver / importance
  // (CUSTOMIZED_ROLES). Anything else is a contract break → fail fast.
  switch (task.role) {
    case 'npiDriver':
      return 'customizedAnalysis_npiDriver'
    case 'importance':
      return 'customizedAnalysis_importance'
    default:
      throw new Error(`basic-output: unexpected customized role ${task.role}`)
  }
}

const generateAnalysisCommentsMutationDocument = graphql(`
  mutation BasicOutputGenerationPage_GenerateAnalysisComments(
    $surveyId: String!
    $outputs: [AnalysisCommentInput!]!
    $ownBrandNames: [String!]
    $keyCompetitorBrandNames: [String!]
    $outputLanguage: OutputLanguage!
    $origin: String
  ) {
    generateAnalysisComments(
      surveyId: $surveyId
      outputs: $outputs
      ownBrandNames: $ownBrandNames
      keyCompetitorBrandNames: $keyCompetitorBrandNames
      outputLanguage: $outputLanguage
      origin: $origin
    ) {
      awaitId
    }
  }
`)

const generateAnalysisCommentsStatusQueryDocument = graphql(`
  query BasicOutputGenerationPage_GenerateAnalysisCommentsStatus(
    $awaitId: String!
  ) {
    generateAnalysisCommentsStatus(awaitId: $awaitId) {
      arrived
      error
      progress {
        completed
        total
      }
      result {
        comments {
          brandName
          comment
          filterPatternIndex
          outputKey
          outputType
          segmentMode
        }
      }
    }
  }
`)

// Flat map keyed solely by the unique outputKey carried end-to-end (mint → send →
// echo → lookup). No composite rebuild, no per-filter nesting, no fallback.
export type CommentsMap = Map<string, string>

// Correlate analyzer comments strictly by the unique outputKey carried
// end-to-end. Every output sends one, so a null echo is a backend contract
// break — skip it rather than mis-correlate. Shared by the success path and the
// partial-error path: on 'timeout' the analyzer still returns whatever comments
// the job produced alongside the error, so both paths must parse identically.
const correlateCommentsByOutputKey = (
  comments: readonly { comment: string; outputKey?: string | null }[],
): CommentsMap => {
  const commentsMap: CommentsMap = new Map()
  for (const c of comments) {
    if (c.outputKey == null) {
      continue
    }
    commentsMap.set(c.outputKey, c.comment)
  }

  return commentsMap
}

type CommentProgress = {
  completed: number
  commentsMap: CommentsMap
  isComplete: boolean
  total: number
}

// Mirrors useRenderAndSaveDocumentImages' StartContext so the future dispatcher
// has ONE consistent wizard-input contract feeding planRenderTasks. The extra
// `brandRoleContext` field carries own/competitor brand ids for prompt name
// resolution (comments-only; the renderer does not need it).
type StartContext = {
  // Always true on this path (the flow skips straight to 'documenting' with AI off), but
  // threaded rather than hardcoded so all three planRenderTasks callers read one source.
  aiCommentsEnabled: boolean
  brandGroupId: number
  brandIdToOriginalIndex: Map<number, number>
  brandRoleContext: {
    brands: readonly { id: number; name: string }[]
    keyCompetitorBrandIds: readonly number[]
    ownBrandIds: readonly number[]
  }
  customerAttributeGroups: {
    customerAttributes: { id: string; name: string }[]
    id: string
    name: string
  }[]
  customRoleAxes: Record<string, 'customerAttribute' | 'question' | undefined>
  filterPatternFilterGroups: SurveyFilterGroups[]
  getBrandAligned: (role: string) => boolean
  isEntireBrandGroupSelected: boolean
  questionAssignments: Record<string, string[]>
  questions: {
    mt_questions: string[]
    question_id: string
    question_text: string
  }[]
  result: BasicOutputsResult
  selectedBrandIds: number[] // explicit; may be empty when isEntireBrandGroupSelected is true — forwarded to planRenderTasks
  surveyId: string
}

export const useGenerateAnalysisComments = () => {
  const isMounted = useMountedState()
  // Monotonic run id. Each start() claims the next value; only the run whose id
  // still matches runIdRef.current is "active". A superseded run is abandoned
  // (it never settles its polling promise — see poll()), so a stale poll can
  // never overwrite the current run's progress/result.
  const runIdRef = React.useRef(0)
  const graphQLRequest = useGraphQLRequest()
  const survey = useSurveyQueryData()
  const { t } = useTranslation()
  const [progress, setProgress] = React.useState<CommentProgress>(() => ({
    completed: 0,
    commentsMap: new Map(),
    isComplete: false,
    total: 0,
  }))

  const start = React.useCallback(
    async (context: StartContext): Promise<CommentsMap> => {
      // Claim this run. Any later start() bumps runIdRef, superseding us.
      const runId = ++runIdRef.current
      // Absolute wall-clock bound for the whole poll loop. A stuck job must not
      // strand the user on an endless spinner, so past this point we degrade
      // gracefully instead of rescheduling forever.
      const deadlineAt = Date.now() + 10 * 60 * 1000

      const {
        aiCommentsEnabled,
        brandGroupId,
        brandIdToOriginalIndex,
        brandRoleContext,
        customerAttributeGroups,
        customRoleAxes,
        filterPatternFilterGroups,
        getBrandAligned,
        isEntireBrandGroupSelected,
        questionAssignments,
        questions,
        result,
        selectedBrandIds,
        surveyId,
      } = context

      assertExists(survey, t('assertion_failed'))

      const brandGroup = survey.brandGroups.find((bg) => bg.id === brandGroupId)
      const groupBrands = (brandGroup?.brands ?? []).map((b) => ({
        id: b.id,
        name: b.name,
        number: b.number,
      }))

      // PLANNING is engine-free: the count/identity of tables comes from wizard
      // input alone (see planRenderTasks). The engine result only fills content.
      const tasks = planRenderTasks({
        aiCommentsEnabled,
        brandIdToOriginalIndex,
        customerAttributeGroups,
        customRoleAxes,
        filterPatternDrafts: filterPatternFilterGroups.map((_, index) => ({
          name: result.filterResults[index]?.filterName ?? '',
        })),
        getBrandAligned,
        groupBrands,
        isEntireBrandGroupSelected,
        ownBrandIds: brandRoleContext.ownBrandIds,
        questionAssignments,
        questions,
        selectedBrandIds,
      })

      const buildTaskElements = createTaskElementsBuilder({
        customerAttributeGroups,
        questions,
        result,
        // Same source + filter as planRenderTasks `targetBrands` and the analyzer
        // resolver's group-filter brand list → resolver-matching index order.
        selectedBrandsInOrder: groupBrands.filter((b) =>
          selectedBrandIds.includes(b.id),
        ),
        surveyQuestions: survey.questions,
        t,
      })

      const outputs: {
        brandName: string
        filterDescription: string
        filterPatternIndex: number
        outputKey: string
        outputType: OutputType
        segmentMode: SegmentMode
        tabulationData: string
      }[] = []

      for (const task of tasks) {
        // Build is engine-data-dependent and the plan fixes the output count, so
        // a missing output / builder error is a real data bug — FAIL FAST.
        const tableElements = buildTaskElements(task)
        const el = tableElements.find((e) => e.segmentMode === task.segmentMode)
        if (!exists(el)) {
          throw new Error(
            `basic-output: no ${task.segmentMode} table for kind=${task.kind}, role=${task.role}, brandId=${task.brandId}`,
          )
        }

        const outputType: OutputType = toCommentOutputType(task)

        // Human-readable filter CONDITION for the prompt's `# 対象条件` block.
        // The "ALL"/no-filter pattern has no groups → emit '' so the analyzer
        // drops the block entirely (no 全データ label).
        const filterGroups = filterPatternFilterGroups[task.filterIndex] ?? []
        const filterDescription =
          filterGroups.length === 0
            ? ''
            : formatFilterTextGroups(
                buildSurveyFilterTextGroups({
                  brandGroups: survey.brandGroups,
                  customerAttributeGroups: survey.customerAttributeGroups,
                  filterGroups,
                  questions: survey.questions,
                  t,
                }),
              )

        outputs.push({
          // The analyzer resolves its [MyBrand] prompt token from this name (brandTokens:
          // `brandName` wins over owns[0] when it is an own brand), so a group-wide task
          // sends its framing 自社 brand — that is the whole point of the fan-out.
          brandName: task.brandName ?? task.framingBrandName ?? 'all',
          filterDescription,
          filterPatternIndex: task.filterIndex,
          outputKey: task.outputKey,
          outputType,
          // Send the SegmentMode enum value the analyzer expects ('fiveSegs' /
          // 'nineSegs'), not the wizard tag. This also makes segmentModeToPrompt
          // resolve correctly server-side (the wizard '5segs' previously fell
          // through to 'none').
          segmentMode: toCommentSegmentMode(task.segmentMode),
          tabulationData: el.textContent,
        })
      }

      if (outputs.length === 0) {
        setProgress({
          completed: 0,
          commentsMap: new Map(),
          isComplete: true,
          total: 0,
        })

        return new Map()
      }

      setProgress({
        completed: 0,
        commentsMap: new Map(),
        isComplete: false,
        total: outputs.length,
      })

      // Resolve role IDs → names exactly once. Roles are independent of 生成対象
      // selection, so role brands not in selectedBrandIds still flow to the
      // prompt. Iterating the full group brand list filters out stale role IDs
      // whose brand vanished and yields brand-group display order. The two `if`s
      // are independent — a brand in BOTH arrays appears in BOTH payloads.
      const ownBrandNames: string[] = []
      const keyCompetitorBrandNames: string[] = []
      for (const brand of brandRoleContext.brands) {
        if (brandRoleContext.ownBrandIds.includes(brand.id)) {
          ownBrandNames.push(brand.name)
        }
        if (brandRoleContext.keyCompetitorBrandIds.includes(brand.id)) {
          keyCompetitorBrandNames.push(brand.name)
        }
      }

      let awaitId: string | undefined
      try {
        const mutationResult = await graphQLRequest({
          document: generateAnalysisCommentsMutationDocument,
          variables: {
            keyCompetitorBrandNames:
              keyCompetitorBrandNames.length > 0
                ? keyCompetitorBrandNames
                : undefined,
            origin: 'wizard',
            outputLanguage: toOutputLanguage(i18n.language),
            outputs,
            ownBrandNames: ownBrandNames.length > 0 ? ownBrandNames : undefined,
            surveyId,
          },
        })
        awaitId = mutationResult.generateAnalysisComments?.awaitId ?? undefined
      } catch (error) {
        // A superseded (a newer start() bumped runIdRef) or unmounted run must
        // not surface a spurious error on the stale path nor write progress.
        if (runId !== runIdRef.current || !isMounted()) {
          // Abandon: never settle (consistent with poll()'s supersede guards) so the
          // caller's stale .then never runs and cannot overwrite the current run.
          return new Promise<CommentsMap>(() => {})
        }
        console.error('generateAnalysisComments mutation failed:', error)
        // FAIL FAST: a failed initial mutation must not resolve an empty map as
        // success (that silently drops every comment). Mark progress terminal
        // and rethrow so the caller's .catch logs and resets the phase, matching
        // the polling path's fatal handling.
        setProgress((prev) => ({ ...prev, isComplete: true }))
        throw error instanceof Error
          ? error
          : new Error(`generateAnalysisComments mutation failed: ${error}`)
      }

      if (!exists(awaitId)) {
        // A missing awaitId is a failed submit, not a no-op: treat it as fatal
        // for the same reason as a rejected mutation above.
        setProgress((prev) => ({ ...prev, isComplete: true }))
        throw new Error(
          'basic-output: generateAnalysisComments returned no awaitId',
        )
      }

      return await new Promise<CommentsMap>((resolve, reject) => {
        const poll = async () => {
          // INVARIANT: the active run settles this promise exactly once; a
          // superseded run (a newer start() bumped runIdRef) intentionally does
          // NOT settle — it returns here and is abandoned, so its stale state
          // can never clobber the newer run. This is deliberate, not a leak.
          if (runId !== runIdRef.current) {
            return
          }

          if (!isMounted()) {
            resolve(new Map())

            return
          }

          try {
            const queryResult = await graphQLRequest({
              document: generateAnalysisCommentsStatusQueryDocument,
              variables: { awaitId },
            })

            // The await above can finish after a newer run started or after
            // unmount; gate every post-await write on both before touching state.
            if (runId !== runIdRef.current || !isMounted()) {
              return
            }

            const status = queryResult.generateAnalysisCommentsStatus

            // A null status is NOT "still pending" — the await resolved with no
            // record, so rescheduling would spin the spinner forever. Degrade
            // gracefully: log, mark terminal, resolve empty.
            if (!exists(status)) {
              console.warn(
                `basic-output: comment status not found for awaitId ${awaitId}`,
              )
              setProgress((prev) => ({ ...prev, isComplete: true }))
              resolve(new Map())

              return
            }

            if (!status.arrived) {
              const statusProgress = status.progress
              if (exists(statusProgress)) {
                setProgress((prev) => ({
                  ...prev,
                  completed: statusProgress.completed,
                  total: statusProgress.total,
                }))
              }
              // Keep polling only while inside the absolute deadline; past it,
              // degrade like a missing status rather than spinning forever.
              if (Date.now() < deadlineAt) {
                setTimeout(poll, 2000)
              } else {
                console.warn(
                  `basic-output: comment generation deadline exceeded for awaitId ${awaitId}`,
                )
                setProgress((prev) => ({ ...prev, isComplete: true }))
                resolve(new Map())
              }

              return
            }

            // status.arrived === true. Parse any (possibly partial) comments
            // FIRST so the error path below resolves what the job produced
            // instead of discarding it.
            const commentsMap: CommentsMap = exists(status.result)
              ? correlateCommentsByOutputKey(status.result.comments)
              : new Map()

            if (exists(status.error)) {
              // The LLM comment job reported a server-side error (e.g.
              // 'timeout'). AI comments are best-effort enrichment, and the
              // analyzer returns whatever comments it produced alongside the
              // error — resolve those partial results rather than dropping
              // them. An empty result simply yields an empty map and document
              // generation proceeds without comments.
              console.error(
                'analysis comments finished with error:',
                status.error,
              )
            }

            setProgress({
              completed: outputs.length,
              commentsMap,
              isComplete: true,
              total: outputs.length,
            })
            resolve(commentsMap)
          } catch (error) {
            // A newer run superseding us, or an unmount, is not a failure of
            // THIS run — stay abandoned (do not reject, which would surface a
            // spurious error on the active run's caller, nor write progress).
            if (runId !== runIdRef.current || !isMounted()) {
              return
            }
            console.error('Polling for analysis comments failed:', error)
            // FAIL FAST: a transient poll error must not resolve an empty map as
            // success (that silently drops every comment). Mark progress
            // terminal and reject so the caller's .catch surfaces it.
            setProgress((prev) => ({ ...prev, isComplete: true }))
            reject(
              error instanceof Error
                ? error
                : new Error(`polling for analysis comments failed: ${error}`),
            )
          }
        }

        poll()
      })
    },
    [graphQLRequest, isMounted, survey, t],
  )

  return { progress, start }
}
